import { reactive } from 'vue';
import {
  ADD_ANNOUNCEMENT_ROUTE,
  ADD_MINYANIM_ROLL_N_ROUTE,
  ADD_SHIUREI_TORAH_ROUTE,
  ADD_ZMANIM_N_ROUTE,
  ANNOUNCEMENTS_N_ROUTE,
  API_KEYS_ROUTE,
  DASHBOARD_ROUTE,
  DATE_TIME_BANNER_ROUTE,
  DOCS_ANNOUNCEMENT_ROUTE,
  DOCS_AUTHENTICATION_ROUTE,
  DOCS_DONATION_ROUTE,
  DOCS_HOME_ROUTE,
  DOCS_KABBALAH_ROUTE,
  DOCS_SCREEN_ROUTE,
  DOCS_TEMPLATE_ROUTE,
  DONATIONS_N_ROUTE,
  DUPLICATE_ANNOUNCEMENT_ROUTE,
  DUPLICATE_MINYANIM_ROLL_N_ROUTE,
  DUPLICATE_SHIUREI_TORAH_ROUTE,
  DUPLICATE_ZMANIM_N_ROUTE,
  EDIT_ANNOUNCEMENT_ROUTE,
  EDIT_MINYANIM_ROLL_N_ROUTE,
  EDIT_SHIUREI_TORAH_ROUTE,
  EDIT_ZMANIM_N_ROUTE,
  KABBALAH_N_ROUTE,
  MINYANIM_ROLLS_N_ROUTE,
  POSTERS_ROUTE,
  SETTINGS_ROUTE,
  SHIUREI_TORAH_N_ROUTE,
  TICKETS_ROUTE,
  ZMANIM_N_ROUTE,
} from '@/router/routes';

/**
 * KodeAI — the in-app help assistant (components/kode_ai/Kode_AI_Widget).
 *
 * What the widget shares with nothing else but is better kept out of it: the
 * limits the API holds a question to (its utils/kode_ai), how an answer's
 * markdown becomes safe HTML, how a page is named to the model, and the
 * browser's speech: dictation in (Web Speech API) and reading answers aloud
 * (speech synthesis).
 */

export const KODE_AI_NAME = 'KodeAI';

/* -------------------------------- limits -------------------------------- */
// Mirrors the API (its utils/kode_ai), so a bad question is caught here.

export const KODE_AI_MESSAGE_MAX_LENGTH = 2000;
export const KODE_AI_HISTORY_MAX_TURNS = 20;
export const KODE_AI_HISTORY_TURN_MAX_LENGTH = 6000;
/** The most a recorded question may run; the recorder stops itself there. */
export const KODE_AI_RECORDING_MAX_SECONDS = 90;
/** How many messages the panel keeps between reloads. */
export const KODE_AI_KEEP_MESSAGES = 40;

/* ------------------------------ the panel ------------------------------- */
// Whether the panel is open, shared by the widget (mounted once, in App.vue)
// and the buttons that open it: "Ask KodeAI" in the desktop top bar
// (components/Navbar) and the "k" in each page's phone top bar
// (components/kode_ai/Kode_AI_Button).

export const kodeAiPanel = reactive({
  /** The widget is on the page: the buttons show only then. */
  ready: false,
  open: false,
});

// The button the panel was opened from: focus goes back to it on closing.
let openedFrom: HTMLElement | null = null;

export function openKodeAi(trigger: HTMLElement | null = null): void {
  if (!kodeAiPanel.ready) return;
  openedFrom = trigger;
  kodeAiPanel.open = true;
}

/** Closes the panel; the conversation stays. `restoreFocus: false` when something else is taking the user elsewhere. */
export function closeKodeAi({ restoreFocus = true }: { restoreFocus?: boolean } = {}): void {
  kodeAiPanel.open = false;
  const target = openedFrom;
  openedFrom = null;
  if (restoreFocus && target && target.isConnected) target.focus({ preventScroll: true });
}

export function toggleKodeAi(trigger: HTMLElement | null = null): void {
  if (kodeAiPanel.open) closeKodeAi();
  else openKodeAi(trigger);
}

/* ------------------------------- storage -------------------------------- */

export const KODE_AI_OPEN_KEY = 'screenkode.kodeAi.open';
export const KODE_AI_MESSAGES_KEY = 'screenkode.kodeAi.messages';
export const KODE_AI_VOICE_REPLIES_KEY = 'screenkode.kodeAi.voiceReplies';

/* -------------------------------- shapes -------------------------------- */

export type KodeAiRole = 'user' | 'assistant';

export interface KodeAiMessage {
  id: string;
  role: KodeAiRole;
  text: string;
  /** Only on a question: still being answered, answered, or not sent. */
  status?: 'sending' | 'done' | 'failed';
  /** The question was spoken, not typed. */
  byVoice?: boolean;
  /** What the API answered with, on an answer. */
  model?: string;
  createdAt: string;
}

/** A turn as the API takes it with each question (the conversation so far). */
export interface KodeAiTurn {
  role: 'user' | 'model';
  text: string;
}

export interface KodeAiPage {
  title: string;
  path: string;
}

/* -------------------------------- prompts ------------------------------- */

/** Questions offered on an empty conversation, one click each. */
export const KODE_AI_SUGGESTIONS = [
  'How do I create a donation?',
  'How do I add an announcement?',
  'How do I set up a Minyanim roll?',
  'How do I put a poster on a screen?',
  'Where do I change the scroll speed?',
  'How do I get the link for a screen?',
];

/** The earlier messages as the API wants them: finished turns, oldest first. */
export function kodeAiHistoryOf(messages: KodeAiMessage[]): KodeAiTurn[] {
  return messages
    .filter((message) => message.text && message.status !== 'failed' && message.status !== 'sending')
    .slice(-KODE_AI_HISTORY_MAX_TURNS)
    .map((message): KodeAiTurn => ({
      role: message.role === 'assistant' ? 'model' : 'user',
      text: message.text.length > KODE_AI_HISTORY_TURN_MAX_LENGTH ? message.text.slice(0, KODE_AI_HISTORY_TURN_MAX_LENGTH) : message.text,
    }));
}

let nextId = 0;
export function kodeAiMessageId(): string {
  nextId += 1;
  return `${Date.now().toString(36)}-${nextId}`;
}

/* --------------------------------- pages -------------------------------- */

const PAGE_TITLES: Record<string, string> = {
  [DASHBOARD_ROUTE]: 'Dashboard',
  [ANNOUNCEMENTS_N_ROUTE]: 'Announcements',
  [ADD_ANNOUNCEMENT_ROUTE]: 'Register Announcement',
  [EDIT_ANNOUNCEMENT_ROUTE]: 'Edit Announcement',
  [DUPLICATE_ANNOUNCEMENT_ROUTE]: 'Duplicate Announcement',
  [SHIUREI_TORAH_N_ROUTE]: 'Shiurei Torah',
  [ADD_SHIUREI_TORAH_ROUTE]: 'Register Shiurei Torah',
  [EDIT_SHIUREI_TORAH_ROUTE]: 'Edit Shiurei Torah',
  [DUPLICATE_SHIUREI_TORAH_ROUTE]: 'Duplicate Shiurei Torah',
  [POSTERS_ROUTE]: 'Posters',
  [DONATIONS_N_ROUTE]: 'Donations',
  [MINYANIM_ROLLS_N_ROUTE]: 'Minyanim',
  [ADD_MINYANIM_ROLL_N_ROUTE]: 'Create Minyanim Roll',
  [EDIT_MINYANIM_ROLL_N_ROUTE]: 'Edit Minyanim Roll',
  [DUPLICATE_MINYANIM_ROLL_N_ROUTE]: 'Duplicate Minyanim Roll',
  [ZMANIM_N_ROUTE]: 'Zmanim',
  [ADD_ZMANIM_N_ROUTE]: 'Create Zmanim',
  [EDIT_ZMANIM_N_ROUTE]: 'Edit Zmanim',
  [DUPLICATE_ZMANIM_N_ROUTE]: 'Duplicate Zmanim',
  [DATE_TIME_BANNER_ROUTE]: 'Date Time Banner',
  [API_KEYS_ROUTE]: 'API Keys',
  [TICKETS_ROUTE]: 'Tickets',
  [SETTINGS_ROUTE]: 'Settings',
  [KABBALAH_N_ROUTE]: 'Kabbalah',
  [DOCS_HOME_ROUTE]: 'API documentation',
  [DOCS_AUTHENTICATION_ROUTE]: 'API documentation — Authentication',
  [DOCS_SCREEN_ROUTE]: 'API documentation — Screens',
  [DOCS_TEMPLATE_ROUTE]: 'API documentation — Templates',
  [DOCS_DONATION_ROUTE]: 'API documentation — Donations',
  [DOCS_ANNOUNCEMENT_ROUTE]: 'API documentation — Announcements',
  [DOCS_KABBALAH_ROUTE]: 'API documentation — Kabbalos',
};

/** The page the user is on, as the model is told it: its name and path. */
export function kodeAiPageOf(route: { name?: unknown; path?: string } | null | undefined): KodeAiPage | null {
  if (!route) return null;
  const name = typeof route.name === 'string' ? route.name : '';
  const title = PAGE_TITLES[name] || '';
  const path = String(route.path || '');
  if (!title && !path) return null;
  return { title, path };
}

/* ------------------------------- markdown ------------------------------- */
// The answers come as light markdown — bold labels, numbered steps, links to
// pages. Rendered here into HTML with every character of the model's text
// escaped first, so nothing it says can run or style anything. Links are
// kept only to a page of this app (data-kai-link="internal", which the
// widget routes) or to http(s).

const escapeHtml = (text: string): string =>
  text.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      default: return '&#39;';
    }
  });

const safeHref = (raw: string): { href: string; internal: boolean } | null => {
  const url = raw.trim();
  // A placeholder path such as /announcements/edit/<id> is no page to go to.
  if (/[<>]|&lt;|&gt;/.test(url)) return null;
  if (/^\/(?!\/)[^\s]*$/.test(url)) return { href: url, internal: true };
  if (/^https?:\/\/[^\s]+$/i.test(url)) return { href: url, internal: false };
  return null;
};

const inlineMarkdown = (text: string): string => {
  let out = escapeHtml(text);
  out = out.replace(/`([^`\n]+)`/g, '<code>$1</code>');
  out = out.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, (_match, label: string, url: string) => {
    const link = safeHref(url);
    if (!link) return label;
    return link.internal
      ? `<a href="${link.href}" data-kai-link="internal">${label}</a>`
      : `<a href="${link.href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });
  out = out.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,;:!?]|$)/g, '$1<em>$2</em>');
  out = out.replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,;:!?]|$)/g, '$1<em>$2</em>');
  return out;
};

const ORDERED_ITEM = /^\s*(\d+)[.)]\s+(.*)$/;
const UNORDERED_ITEM = /^\s*[-*•]\s+(.*)$/;
const HEADING = /^\s*#{1,6}\s+(.*)$/;
const BLOCK_START = /^\s*(\d+[.)]|[-*•]|#{1,6})\s+/;

/** An answer's markdown as HTML: paragraphs, numbered and bulleted lists, bold, italics, code and links. */
export function renderKodeMarkdown(markdown: string): string {
  const lines = String(markdown || '').replace(/\r\n?/g, '\n').split('\n');
  const html: string[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const ordered = ORDERED_ITEM.exec(line);
    const unordered = UNORDERED_ITEM.exec(line);
    if (ordered || unordered) {
      const isOrdered = Boolean(ordered);
      const start = ordered ? Number(ordered[1]) : 1;
      const items: string[] = [];
      while (index < lines.length) {
        const current = lines[index];
        const orderedItem = ORDERED_ITEM.exec(current);
        const unorderedItem = UNORDERED_ITEM.exec(current);
        const item = isOrdered ? orderedItem : unorderedItem;
        if (item) {
          items.push(inlineMarkdown(isOrdered ? item[2] : item[1]));
          index += 1;
          continue;
        }
        // An indented line under an item carries on that item.
        if (current.trim() && /^\s{2,}/.test(current) && !orderedItem && !unorderedItem && items.length) {
          items[items.length - 1] += `<br>${inlineMarkdown(current.trim())}`;
          index += 1;
          continue;
        }
        break;
      }
      html.push(isOrdered ? `<ol start="${start}">` : '<ul>');
      items.forEach((item) => html.push(`<li>${item}</li>`));
      html.push(isOrdered ? '</ol>' : '</ul>');
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      html.push(`<p class="kai-md__heading">${inlineMarkdown(heading[1])}</p>`);
      index += 1;
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length && lines[index].trim() && !BLOCK_START.test(lines[index])) {
      paragraph.push(inlineMarkdown(lines[index].trim()));
      index += 1;
    }
    html.push(`<p>${paragraph.join('<br>')}</p>`);
  }

  return html.join('');
}

/** An answer as plain words, for reading aloud or copying. */
export function kodeAiPlainText(markdown: string): string {
  return String(markdown || '')
    .replace(/\[([^\]\n]+)\]\([^)\s]+\)/g, '$1')
    .replace(/[*_`#]+/g, '')
    .replace(/^\s*\d+[.)]\s+/gm, '')
    .replace(/^\s*[-•]\s+/gm, '')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
}

/* -------------------------------- speech -------------------------------- */

// Hebrew or Yiddish letters anywhere: the voice (and the dictation) is Hebrew.
const HEBREW = /[֐-׿]/;

const languageOf = (text: string): string => {
  if (HEBREW.test(text)) return 'he-IL';
  return (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
};

/* Dictation (Web Speech API) — TypeScript 4.4+ dropped these from lib.dom. */

interface DictationAlternative {
  transcript: string;
}

interface DictationResult {
  isFinal: boolean;
  length: number;
  [index: number]: DictationAlternative;
}

interface DictationEvent {
  resultIndex: number;
  results: { length: number; [index: number]: DictationResult };
}

interface DictationErrorEvent {
  error: string;
}

interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: DictationEvent) => void) | null;
  onerror: ((event: DictationErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type RecognitionConstructor = new () => Recognition;

const recognitionConstructor = (): RecognitionConstructor | null => {
  if (typeof window === 'undefined') return null;
  const scope = window as unknown as { SpeechRecognition?: RecognitionConstructor; webkitSpeechRecognition?: RecognitionConstructor };
  return scope.SpeechRecognition || scope.webkitSpeechRecognition || null;
};

/** Whether this browser can turn speech into text as it is spoken (Chrome, Edge, Safari). */
export const isDictationSupported = (): boolean => recognitionConstructor() !== null;

/** Whether this browser can record a question for the API to transcribe (nearly all). */
export const isRecordingSupported = (): boolean =>
  typeof window !== 'undefined'
  && typeof navigator !== 'undefined'
  && Boolean(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function')
  && typeof (window as unknown as { MediaRecorder?: unknown }).MediaRecorder === 'function';

export interface KodeDictationHandlers {
  /** What has been understood so far: the settled words, then the ones still being guessed. */
  onResult(finalText: string, interimText: string): void;
  /** Dictation stopped — by itself (silence), or because stop() was called. */
  onEnd(): void;
  /** Why it could not go on: not-allowed, no-speech, network, audio-capture, aborted… */
  onError(code: string): void;
}

export interface KodeDictation {
  start(): void;
  stop(): void;
  abort(): void;
}

/**
 * Dictation into the composer: one question at a time, with the words shown
 * as they are recognized. Null where the browser cannot do it.
 */
export function createKodeDictation(handlers: KodeDictationHandlers, lang?: string): KodeDictation | null {
  const Constructor = recognitionConstructor();
  if (!Constructor) return null;
  const recognition = new Constructor();
  recognition.lang = lang || languageOf('');
  recognition.continuous = false;
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;

  recognition.onresult = (event) => {
    let finalText = '';
    let interimText = '';
    for (let index = 0; index < event.results.length; index += 1) {
      const result = event.results[index];
      const transcript = result[0] ? result[0].transcript : '';
      if (result.isFinal) finalText += transcript;
      else interimText += transcript;
    }
    handlers.onResult(finalText.trim(), interimText.trim());
  };
  recognition.onerror = (event) => handlers.onError(event.error || 'unknown');
  recognition.onend = () => handlers.onEnd();

  return {
    start: () => recognition.start(),
    stop: () => recognition.stop(),
    abort: () => recognition.abort(),
  };
}

/* Reading aloud (speech synthesis). */

export const isSpeechSupported = (): boolean =>
  typeof window !== 'undefined' && 'speechSynthesis' in window && typeof window.SpeechSynthesisUtterance === 'function';

// Long utterances stall in some browsers (Chrome stops after ~15 s): an
// answer is read a sentence or two at a time.
const SPEECH_CHUNK_MAX = 220;

const chunksOf = (text: string): string[] => {
  // Sentence by sentence (no lookbehind: older Safari cannot parse one).
  const sentences = text.replace(/\s+/g, ' ').trim().match(/[^.!?:;]+[.!?:;]*/g) || [];
  const chunks: string[] = [];
  let current = '';
  sentences.forEach((sentence) => {
    if (!sentence) return;
    if ((current + ' ' + sentence).trim().length > SPEECH_CHUNK_MAX && current) {
      chunks.push(current.trim());
      current = sentence;
    } else {
      current = `${current} ${sentence}`;
    }
  });
  if (current.trim()) chunks.push(current.trim());
  return chunks;
};

let speakingToken = 0;

/** Reads an answer aloud; onEnd fires when it is finished or stopped. */
export function speakKodeAnswer(markdown: string, onEnd: () => void): void {
  if (!isSpeechSupported()) {
    onEnd();
    return;
  }
  const synth = window.speechSynthesis;
  synth.cancel();
  speakingToken += 1;
  const token = speakingToken;
  const text = kodeAiPlainText(markdown);
  const chunks = chunksOf(text);
  if (!chunks.length) {
    onEnd();
    return;
  }
  const lang = languageOf(text);
  const voices = synth.getVoices ? synth.getVoices() : [];
  const voice = voices.find((candidate) => candidate.lang && candidate.lang.toLowerCase().startsWith(lang.slice(0, 2).toLowerCase())) || null;

  let remaining = chunks.length;
  const finish = () => {
    remaining -= 1;
    if (remaining <= 0 && token === speakingToken) onEnd();
  };
  chunks.forEach((chunk) => {
    const utterance = new SpeechSynthesisUtterance(chunk);
    utterance.lang = lang;
    if (voice) utterance.voice = voice;
    utterance.rate = 1;
    utterance.onend = finish;
    utterance.onerror = finish;
    synth.speak(utterance);
  });
}

export function stopSpeaking(): void {
  if (!isSpeechSupported()) return;
  speakingToken += 1;
  window.speechSynthesis.cancel();
}

/* ------------------------------- recording ------------------------------ */

// What the browser can record in, best first: Opus in WebM (Chrome, Edge,
// Firefox), then MP4 / AAC (Safari), then Ogg — all of which the API takes.
const RECORDING_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/aac', 'audio/ogg;codecs=opus', 'audio/ogg'];

export function pickRecordingMimeType(): string {
  const Recorder = (window as unknown as { MediaRecorder?: { isTypeSupported?: (type: string) => boolean } }).MediaRecorder;
  if (!Recorder || typeof Recorder.isTypeSupported !== 'function') return '';
  return RECORDING_MIME_TYPES.find((type) => Recorder.isTypeSupported!(type)) || '';
}

/** A recording as the API takes it: its MIME type and its bytes, base64. */
export function recordingToBase64(blob: Blob): Promise<{ mimeType: string; data: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('The recording could not be read.'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve({
        mimeType: (blob.type || 'audio/webm').split(';')[0].trim().toLowerCase(),
        data: comma >= 0 ? result.slice(comma + 1) : result,
      });
    };
    reader.readAsDataURL(blob);
  });
}

/** "0:42" */
export function formatSeconds(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${rest < 10 ? '0' : ''}${rest}`;
}

/* ------------------------------- storage -------------------------------- */
// Storage that may be blocked (a private window, a support session's frame).

export const readStorage = (storage: 'sessionStorage' | 'localStorage', key: string): string | null => {
  try {
    return window[storage].getItem(key);
  } catch (error) {
    return null;
  }
};

export const writeStorage = (storage: 'sessionStorage' | 'localStorage', key: string, value: string | null): void => {
  try {
    if (value === null) window[storage].removeItem(key);
    else window[storage].setItem(key, value);
  } catch (error) {
    // Not kept; the widget works the same.
  }
};

/** The messages kept from the last visit, if any and if they look right. */
export function readStoredMessages(): KodeAiMessage[] {
  try {
    const raw = readStorage('sessionStorage', KODE_AI_MESSAGES_KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    return list
      .filter((item) => item && typeof item.text === 'string' && (item.role === 'user' || item.role === 'assistant'))
      .map((item): KodeAiMessage => ({
        id: String(item.id || kodeAiMessageId()),
        role: item.role as KodeAiRole,
        text: item.text,
        // A question left mid-way when the page closed is not answered any more.
        status: item.role === 'user' ? (item.status === 'failed' || item.status === 'sending' ? 'failed' : 'done') : undefined,
        byVoice: Boolean(item.byVoice),
        model: typeof item.model === 'string' ? item.model : undefined,
        createdAt: typeof item.createdAt === 'string' ? item.createdAt : new Date().toISOString(),
      }))
      .slice(-KODE_AI_KEEP_MESSAGES);
  } catch (error) {
    return [];
  }
}

export function writeStoredMessages(messages: KodeAiMessage[]): void {
  writeStorage('sessionStorage', KODE_AI_MESSAGES_KEY, messages.length ? JSON.stringify(messages.slice(-KODE_AI_KEEP_MESSAGES)) : null);
}
